import { useState, useEffect } from "react";
import { Menu, X, Moon, Sun, Languages, Code2 } from "lucide-react";
import { useApp } from "../context/AppContext";

const NAV_ITEMS = [
  { key: "nav.home", href: "#home" },
  { key: "nav.about", href: "#about" },
  { key: "nav.experience", href: "#experience" },
  { key: "nav.projects", href: "#projects" },
  { key: "nav.skills", href: "#skills" },
  { key: "nav.ai", href: "#ai" },
  { key: "nav.contact", href: "#contact" },
];

export default function Navbar() {
  const { t, theme, toggleTheme, locale, toggleLocale } = useApp();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-soft backdrop-blur-xl" : ""
      }`}
      style={{ backgroundColor: scrolled ? "rgb(var(--bg) / 0.75)" : "transparent" }}
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <a href="#home" onClick={closeMenu} className="group flex items-center gap-2" aria-label="Daniel Camacho — inicio">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 text-slate-950 shadow-lg shadow-cyan-500/20">
            <Code2 className="h-5 w-5" strokeWidth={2.4} />
          </span>
          <span className="font-display text-lg font-bold tracking-tight">
            Daniel<span className="gradient-text">Camacho</span>
          </span>
        </a>

        {/* Desktop nav */}
        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-soft transition-colors hover:text-cyan-400"
              >
                {t(item.key)}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLocale}
            aria-label={locale === "es" ? "Switch to English" : "Cambiar a Español"}
            className="flex items-center gap-1.5 rounded-lg border border-soft px-2.5 py-2 text-xs font-semibold text-soft transition-colors hover:text-cyan-400"
          >
            <Languages className="h-4 w-4" />
            {t("lang.toggle")}
          </button>
          <button
            onClick={toggleTheme}
            aria-label={theme === "dark" ? t("theme.light") : t("theme.dark")}
            className="rounded-lg border border-soft p-2 text-soft transition-colors hover:text-cyan-400"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <a
            href="#projects"
            className="hidden rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 px-4 py-2 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition-transform hover:scale-[1.03] sm:inline-flex lg:inline-flex"
          >
            {t("nav.cta")}
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            className="rounded-lg border border-soft p-2 text-soft transition-colors hover:text-cyan-400 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden border-soft transition-[max-height,opacity] duration-300 lg:hidden ${
          open ? "max-h-96 border-t opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ backgroundColor: "rgb(var(--bg) / 0.98)" }}
      >
        <ul className="flex flex-col gap-1 px-5 py-4">
          {NAV_ITEMS.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                onClick={closeMenu}
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-soft transition-colors hover:bg-cyan-400/10 hover:text-cyan-400"
              >
                {t(item.key)}
              </a>
            </li>
          ))}
          <li className="pt-2">
            <a
              href="#projects"
              onClick={closeMenu}
              className="block rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 px-4 py-2.5 text-center text-sm font-semibold text-slate-950"
            >
              {t("nav.cta")}
            </a>
          </li>
        </ul>
      </div>
    </header>
  );
}
